// AUTH

import {
  AuthProviderEnum,
  BatchStatusEnum,
  ChainsEnum,
  ErrorCodeEnum,
  MonitoringStatusEnum,
  PlanPeriods,
} from '@/config';

enum EmailStatusEnum {
  UNCONFIRMED = 'unconfirmed',
  CONFIRMED = 'confirmed',
  CHANGE_REQUESTED = 'change-requested',
}

enum TransactionDirectionEnum {
  SENT = 'sent',
  RECEIVED = 'received',
}

enum SideEnum {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
}

enum CheckTypeEnum {
  AML = '0',
  INVESTIGATION = '1',
}

enum CheckOutputTypeEnum {
  TX = 7,
  ADDRESS = 9,
  ADDRESS_EXPANDED = 10,
  TX_APPROXIMATE = 11,
}

enum AccountModesEnum {
  LITE = 10,
  PRO = 20,
  PRO_PLUS = 30,
}

enum UserPreferenceEnum {
  CHECK = 10,
  AI_TRACE = 20,
  AI_TRACE_INCLUDED = 30,
}

type User = {
  emailAddress: string;
  emailStatus: EmailStatusEnum | null;
  fullName: string;
  hasBillingCard: boolean;
  billingCardBrand: string;
  extBalance: number;
  locale: string;
  discount: number;
  discountDueTime: number;
  refProgram: boolean;
  refCode: string;
  refFeePercent: number;
  refWds: number;
  refBal: number;
  refDiscount: number;
  refCount: number;
  promoBalance: boolean;
  useKyc: boolean;
  kycCost: null;
  isLocked: null;
  needNews: boolean;
  kycLogo: string;
  kycSupportLink: string;
  use2Fa: boolean;
  canClearHistory: boolean;
  amlFlow: string;
  emailChangeCandidate: string;
  provider: AuthProviderEnum;
  termsConfirmRequired: boolean;
  proMode: boolean;
  receiveReminders: boolean;
  is2faAllowed: boolean;
  is2faEnabled: boolean;
  useCompliance: boolean;
  preference?: UserPreferenceEnum | null;
  accountMode: AccountModesEnum;
  amlDetailsFor?: string | null;
};

type BaseRequest = Record<string, never>;

type BaseErrorResponse = {
  success: boolean;
  message: string;
};

type BaseSuccessResponse = {
  success: boolean;
};

type BaseErrorCodeResponse = BaseErrorResponse & {
  errorCode?: ErrorCodeEnum;
};

type CheckRequest = {
  ref?: string;
};

type CheckResponse = {
  success: true;
  user: User;
};

type SignupRequest = {
  emailAddress: string;
  password: string;
  locale?: string;
  needNews?: boolean;
  termsAccepted?: boolean;
  token: string;
  amlCheckFor?: string;
};

type SignupRequestExtended = SignupRequest & {
  hsCallback?: (email: string, username: string) => void;
  gaCallback?: () => void;
};

type SignupResponse = BaseSuccessResponse;

type SigninRequest = {
  emailAddress: string;
  password: string;
  token2fa?: string;
};

type SigninResponse = {
  user: User;
};

type GoogleAuthRequest = {
  code?: string;
  pendingToken?: string;
  token2fa?: string;
  needNews?: boolean;
  locale?: string;
  amlCheckFor?: string;
};

type GoogleAuthResponse = {
  success: boolean;
  user: User;
};

type GoogleAuthOneTapRequest = {
  token: string;
  hsCallback?: (email: string) => void;
  gaCallback?: () => void;
  amlCheckFor?: string;
};

type GoogleAuthOneTapResponse = {
  success: boolean;
  user: User;
};

type TelegramAuthRequest = {
  auth_date: number;
  first_name: string;
  hash: string;
  id: number;
  last_name: string;
  username: string;
  amlCheckFor?: string;
};

type TelegramAuthRequestExtended = TelegramAuthRequest & {
  gaCallback?: () => void;
};

type TelegramAuthResponse = {
  success: boolean;
  user: User;
};

type SignoutRequest = BaseRequest;

type SignoutResponse = BaseSuccessResponse;

type ConfirmEmailRequest = {
  token: string;
};

type ConfirmEmailResponse = BaseSuccessResponse;

type SendConfirmationEmailRequest = BaseRequest;

type SendConfirmationEmailResponse = BaseSuccessResponse;

type ForgotPasswordEmailRequest = {
  emailAddress: string;
};

type ForgotPasswordEmailResponse = BaseSuccessResponse;

type VerifyRecoveryTokenRequest = {
  token: string;
};

type VerifyRecoveryTokenResponse = {
  success: boolean;
  // user: User;
};

type ResetPasswordRequest = {
  token: string;
  password: string;
};

type ResetPasswordResponse = {
  success: boolean;
  // user: User;
};

type ChangeFullNameEmailRequest = {
  fullName: string;
  emailAddress: string;
  password?: string;
};

type UpdateLocaleRequest = {
  locale: string;
};

type UpdateLocaleResponse = {
  success: boolean;
};

type UpdateUserResponse = {
  success: boolean;
  user: User;
};

type ChangeUserPasswordRequest = {
  password: string;
};

type GetTwoFACodeRequest = BaseRequest;

type GetTwoFACodeResponse = {
  success: boolean;
  data: TwoFACode;
};

type UpdateTwoFAUsageRequest = {
  usage: boolean;
  token: string;
};

type UpdateTwoFAUsageResponse = {
  success: boolean;
  data: TwoFAUsage;
};

type UpdateCheckModeRequest = {
  flow: AmlFlowEnum;
};

type UpdateCheckModeResponse = {
  success: boolean;
  data: Partial<User>;
};

type ReceiveRemindersRequest = {
  value: boolean;
};

type ReceiveRemindersResponse = {
  success: boolean;
  data: {
    receiveReminders: boolean;
  };
};

type MonitoringSubscribeRequest = {
  uuid: string;
};

type MonitoringSubscribeResponse = {
  success: boolean;
  data: {
    result: boolean;
    response: Record<string, any>;
  };
};

type MonitoringUnsubscribeRequest = {
  id: number;
};

type MonitoringUnsubscribeResponse = {
  success: boolean;
  data: {
    data: MonitoringAddress;
  };
};

enum AmlFlowEnum {
  ACCURATE = 'accurate',
  ADVANCED = 'advanced',
  FAST = 'fast',
  LIGHT = 'light',
  LITE = 'lite',
  FAST_LIMITED = 'fast_limited',
  ADVANCED_LIMITED = 'advanced_limited',
  INSTANT = 'instant',
}

enum AmlStatusEnum {
  NEW = 'new',
  ERROR = 'error',
  FAILED = 'failed',
  PENDING = 'pending',
  SUCCESS = 'success',
}

enum RiskScoreLevelEnum {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  UNKNOWN = 'unknown',
}

type AmlHistoryItem = {
  uid: string;
  address: string;
  riskscore?: number | boolean;
  risk_score_level?: RiskScoreLevelEnum | null;
  reportedAddressBalance: string | null;
  blackListsConnections: boolean | string;
  hasBlackListFlag: boolean;
  pdfReport: string;
  memo: string;
  asset: string;
  network: string;
  status: AmlStatusEnum;
  flow: AmlFlowEnum;
  updated_at: number; // ms (explorer)
  timestamp: string; // date string (amlbot request)
  signals: Record<string, number>;
  signalTags?: string[];
  addressDetailsData?: Record<string, any>;
  counterparty?: Record<string, any>;
  connections?: Record<string, any>;
  tx?: string;
  originalTx?: string;
  tokenDetails?: Record<string, any>;
  direction?: string;
  amount?: number;
  risky_volume?: number;
  risky_volume_fiat?: number;
  risky_volume_usd?: number | string;
  fiat?: number;
  fiat_code_effective?: string;
  _type?: number;
  extras?: any;
  cost: number;
  customerIsB2B?: boolean;
  hasHighRisk?: boolean;
  devMode?: boolean;
};

type AmlHistory = {
  result: boolean;
  pageLimit: number;
  totalCount: number;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
  usePromoFlow: boolean;
  devModeUsed?: boolean;
  data: AmlHistoryItem[] | InvestigationHistoryItem[];
};

type AmlHistoryRequest =
  | {
      page?: number;
      filterType?: boolean;
      queryHash?: string;
      filter?: Record<string, any>;
    }
  | Record<string, never>;

type AmlHistoryResponse = BaseSuccessResponse & {
  data: AmlHistory;
};

type ExportHistoryResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    totalCount: number;
    data: Record<any, any>[];
  };
};

type AmlCheckRequest = {
  hash: string;
  asset: ChainsEnum;
  side?: SideEnum;
  address?: string;
  force?: boolean;
  type?: CheckTypeEnum;
  token?: number;
  cid?: string;
};

type AmlCheckResponse = BaseSuccessResponse & {
  data: AmlCheck;
};

type InvestigationHistoryItem = {
  indirects?: Record<string, any> | null;
  data?: Record<string, any> | null;
  baseData: Record<string, any> | null;
  sourceData: string[];
  riskscore: number;
  counterparty?: {
    address: string;
  };
  asset: string;
  address: string;
  identifier: string;
  xlsReport: string;
  investigationLink: string;
  stats: {
    connections: number;
    clusters: number;
    directs: number;
    indirects: number;
  };
  uid: string;
  network: string;
  status: AmlStatusEnum;
  timestamp: string; // amlbot
  flow: AmlFlowEnum;
  _type?: number;
  paths?: Record<string, any> | null;
  txs?: Record<string, any> | null;
  tokenData?: Record<string, any> | null;
};

type AmlCheckItem = AmlHistoryItem;

type AmlCheck = {
  result: boolean;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
  usePromoFlow: boolean;
  devModeUsed?: boolean;
  data: AmlCheckItem | InvestigationHistoryItem;
};

type TwoFACode = {
  is2faEnabled: boolean;
  twoFaSecret: string;
  qr2FaCode: string;
};

type TwoFAUsage = {
  is2faEnabled: boolean;
};

type WhichHashRequest = {
  hash: string;
};

type WhichHash = {
  addresses: ChainsEnum[];
  txs: ChainsEnum[];
};

type WhichHashResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    suggestions: WhichHash;
  };
};

type RefData = {
  feePercent: number;
  wds: number;
  bal: number;
  count: number;
  discount: number;
};

type KycFormData = {
  support: string;
};

type BalanceAlert =
  | Record<string, never>
  | {
      level: string;
      period: AlertsPeriodEnum;
      status: string;
    };

enum BonusTypeEnum {
  TYPE_ALL = '-1',
  TYPE_TX = '7',
  TYPE_ADDRESS = '9',
  TYPE_INVESTIGATION = '10',
  TYPE_MONITORING = '100',
}

type BonusItem = {
  type: BonusTypeEnum;
  value: string;
  valid_until: string;
  tech_data?: string;
  tmacnt_id: string;
  flow: AmlFlowEnum;
  name: string;
  create: string;
  timestamp: string;
  active: string;
  archived: string;
  extend: string;
  id: string;
  flow_value: BonusFlowValueEnum;
};

enum BonusFlowValueEnum {
  FLOW_ALL = 'All',
  FLOW_MIXED = 'advanced',
  FLOW_FAST = 'fast',
  FLOW_ACCURATE = 'accurate',
  FLOW_ANY = '-1',
}

enum AlertsPeriodEnum {
  ONE_HOUR = '+1 hour',
  SIX_HOURS = '+6 hours',
  TWELVE_HOURS = '+12 hours',
  ONE_DAY = '+1 day',
}

type ReferralLink = {
  code: string;
  title: string;
  balance: number;
  withdrawals: number;
  referralsCount: number;
  feePercent: number;
  discount: number;
  link: string;
};

type Profile = {
  refLinks: string[];
  refData: RefData;
  kycFormData: KycFormData;
  balanceAlertsSets: BalanceAlert;
  balanceAlertsPeriods: {
    [AlertsPeriodEnum.ONE_HOUR]: string;
    [AlertsPeriodEnum.SIX_HOURS]: string;
    [AlertsPeriodEnum.TWELVE_HOURS]: string;
    [AlertsPeriodEnum.ONE_DAY]: string;
  };
  isAccurateAllowed: boolean;
  bonuses: BonusItem[];
  refLinksData?: ReferralLink[];
  accessKey: string;
  accessId: string;
  usedMonitoring: boolean;
  preferences: Record<any, any>;
  webhookUrl?: string;
  devModeUsed?: boolean;
  isB2B?: boolean;
  accountMode: AccountModesEnum;
  currentPlan: Record<string, any>;
};

type GetProfileInfoRequest = BaseRequest;

type GetProfileInfoResponse = {
  success: boolean;
  data: Profile;
};

type SetBalanceAlertRequest = {
  level: string;
  period: string;
};

type SetBalanceAlertResponse = {
  success: boolean;
  data: BalanceAlert;
};

type DeleteBalanceAlertRequest = BaseRequest;

type DeleteBalanceAlertResponse = BaseSuccessResponse;

type PromoCodeItem = {
  name: string;
  create: string;
  status?: string;
  value: string;
};

type PromoHistory = {
  result: boolean;
  pageLimit: number;
  totalCount: string;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
  data: PromoCodeItem[];
};

type PromoHistoryRequest =
  | {
      page?: number;
    }
  | Record<string, never>;

type PromoHistoryResponse = {
  success: boolean;
  data: PromoHistory;
};

type PromoCode = PromoCodeBase & {
  data: string;
  description?: string;
};

type IssuePromoCodeRequest = {
  amount: number;
};

type IssuePromoCodeResponse = {
  success: boolean;
  data: PromoCode;
};

type CreateReflinkRequest = {
  title: string;
};

type ReflinkResponse = BaseSuccessResponse & {
  data: ReferralLink[];
};

type UpdateReflinkRequest = {
  code: string;
  title: string;
};

type UserPreferencesRequest = {
  preferences: {
    monitoring?: Record<string, any>;
    webhook?: {
      url: string;
    };
  };
  url?: string;
};

type UserPreferencesResponse = BaseSuccessResponse & {
  data: Record<string, any>;
};

type ServiceOptions = {
  result: boolean;
  basePrice: number;
  baseUsdPrice: Record<string, any>;
  assetsAliases: Record<string, any>;
  assetsMinAmounts: Record<string, any>;
  expandedCheckCost: number;
  traceCheckCost: number;
  kycCheckCost: number;
  assetsDiscounts: Record<string, any>[];
  plans: Record<any, any>[];
  availablePlanOptions: Record<any, any>;
  minimumTopUpAmount: number;
  rqType: number;
  asset: string;
  newRendererAfter: string;
  skipAddressCoins: string[];
  notInvestigatedCoins: string[];
  hasTokensCoins: string[];
  limitFlowAssets: string[];
  modeAssets: Record<any, string[]>;
};

type GetServiceOptionsRequest = BaseRequest;

type GetServiceOptionsResponse = BaseSuccessResponse & {
  data: ServiceOptions;
};

type ExploreConnectionRequest = {
  address: string;
  identifier: string;
  target: number;
  type: TransactionDirectionEnum;
  asset?: string;
};

type ExploreConnectionResponse = BaseSuccessResponse & {
  data: {
    data: InvestigationHistoryItem;
  };
};

type AssetToken = {
  issuer: string;
  code: string;
  token_id: number;
  precision: number;
};

type AssetTokenRequest = {
  asset: string;
  page: number;
  term?: string;
};

type AssetTokenResponse = BaseSuccessResponse & {
  data?: {
    hasMore: boolean;
    page: number;
    result: AssetToken[];
  };
  message?: string;
};

type Plan = {
  id: string;
  name: string;
  price: string;
  old_price: string;
  value: string;
  addition: string;
  sku: string;
  options: string[];
  description: string;
  periodicity: PlanPeriods;
  support_only: string;
  options_data: string;
  description_translated: string;
  per_check_str: string;
  isAdvert: boolean;
};

type PlanOptions = {
  checks_number: 'plan_opt_checks_number';
  priority_service: 'plan_opt_priority_service';
  blockchains_and_assets: 'plan_opt_blockchains_and_assets';
  data_sources: 'plan_opt_data_sources';
  automatic_checks: 'plan_opt_automatic_checks';
  manual_checks: 'plan_opt_manual_checks';
  referral_participation: 'plan_opt_referral_participation';
  referral_higher_percentage: 'plan_opt_referral_higher_percentage';
  referral_individual: 'plan_opt_referral_individual';
  aml_kyc_preparation: 'plan_opt_aml_kyc_preparation';
  sanctions_screening: 'plan_opt_sanctions_screening';
  analytics_tools: 'plan_opt_analytics_tools';
  personal_officer: 'plan_opt_personal_officer';
};

type PriceList = {
  result: boolean;
  basePrice: number;
  baseUsdPrice: string;
  allowedAssets: Record<string, string>;
  assetsAliases: Record<string, string>;
  assetsMinAmounts: Record<string, number>;
  expandedCheckCost: number;
  traceCheckCost: number;
  kycCheckCost: number;
  assetsDiscounts: Record<string, number>;
  plans: Plan[];
  availablePlanOptions: PlanOptions;
  minimumTopUpAmount: number;
  stripeEnabled: boolean;
  stripeKey: string;
};

type GetPriceListResponse = BaseSuccessResponse & {
  data: PriceList;
};

type GetPriceListRequest = BaseRequest;

type EnterPromoCodeRequest = {
  type: 'promo';
  promo: string;
};

type EnterPromoCodeResponse = BaseSuccessResponse & {
  data: PromoCodeBase;
};

type PromoCodeBase = {
  result: boolean;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
};

type InvoiceRequest = {
  type: 'plan';
  plan: number;
  asset: string;
  gClientId?: string;
  gSessionId?: string;
};

type StripeInvoiceResponse = BaseSuccessResponse & {
  data: StripeInvoice;
};

type StripeInvoice = {
  result: boolean;
  data: string;
};

type InvoiceResponse = BaseSuccessResponse & {
  data: Invoice;
};

type BaseInvoice = {
  result: boolean;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
};

type Invoice = BaseInvoice & {
  invoice: {
    amount: string;
    address: string;
    asset: string;
    dueTime: string;
  };
};

type InvoiceHistory = {
  id: number;
  plan: {
    name: string;
  };
  paidamount: string;
  base_amount: string;
  asset: string;
  due_time: Date;
  status: string;
  value: string;
  amount: string;
  address: string;
  promo: {
    id: string;
    name: string;
  };
  description: string;
  tmacnt_id: string;
  active: string;
  archived: string;
};

type UserInvoiceHistoryData = BaseInvoice & {
  data: InvoiceHistory[];
  pageLimit: number;
  totalCount: number;
};

type InvoiceHistoryResponse = {
  data: UserInvoiceHistoryData;
};

type GetTxReceiversRequest = {
  asset: ChainsEnum;
  hash: string;
};

type GetTxReceiversResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    data: string[];
  };
};

type GetAmlHistoryItemRequest = {
  uid: string;
};

type GetAmlHistoryItemResultRequest = {
  userData: string;
  responseData: string;
};

type GetAmlHistoryItemResultResponse = {
  success: boolean;
  data: {
    result: boolean;
    data: InvestigationHistoryItem;
  };
};

type GetAmlHistoryItemResponse = {
  result: boolean;
  balance: number;
  amlFlow: AmlFlowEnum;
  discount: number;
  discountDueTime: number;
  promoFlow: boolean;
  data: {
    data: AmlHistoryItem;
  };
};

type CustomTariffRequest = {
  company: string;
  count: string;
  connection: string;
  textbody: string;
};

type CustomTariffResponse = BaseSuccessResponse;

type ExternalPaymentRequest = {
  userData: string;
  invoiceData: string;
};

type ExternalPaymentResponse = {
  success: boolean;
  data: {
    result: boolean;
    data: {
      stripeSessionId: string;
    };
  };
};

type ContactFormRequest = {
  emailAddress: string;
  topic: string;
  fullName: string;
  message: string;
};

type ContactFormResponse = BaseSuccessResponse;

type CheckBatchItem = {
  id: number;
  status: BatchStatusEnum;
  progress: string | number;
  filename: string;
  created: number;
};

type CreateCheckBatchRequest = {
  list: File;
};

type CreateCheckBatchResponse = BaseSuccessResponse & {
  message: string;
  data: CheckBatchItem[];
};

type CheckBatchHistoryRequest = BaseRequest;

type CheckBatchHistoryResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    batches: CheckBatchItem[];
  };
};

type ExportBatchRequest = {
  id: number;
};

type ExportBatchResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    rows: Record<any, any>[];
  };
};

type BatchStateRequest = {
  id: number;
};

type BatchStateResponse = {
  success: boolean;
  data: {
    result: boolean;
    batch: CheckBatchItem;
  };
};

type MonitoringAddress = {
  id: string;
  create: string;
  address: string;
  asset: string;
  tmacnt_id: string;
  next_check_time: string;
  status: MonitoringStatusEnum;
  riskscore: string;
  has_blacklist_flag: string;
  type: string;
  valid_until: string;
};

type AddressMonitoringListRequest = {
  page?: number;
};

type AddressMonitoringListResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    totalCount: number;
    pageLimit: number;
    data: MonitoringAddress[];
  };
};

type UnsubscribeTokenVerifyRequest = {
  token: string;
};

type UnsubscribeTokenVerifyResponse = {
  data: {
    token: boolean;
    user_id: number;
  };
};

type UnsubscribeByTokenRequest = {
  token: string;
  user_id: number;
};

type UnsubscribeByTokenResponse = {
  data: {
    success: boolean;
  };
};

type TestEmailRequest = {
  capture: string;
};

type CompAuthTokenResponse = {
  success: boolean;
  data: {
    result: boolean;
    data: {
      token: string;
      expires: number;
    };
  };
};

type B2BFormTokenRequest = {
  token: string;
};

type B2BFormTokenResponse = BaseSuccessResponse & {
  data: {
    user: string | number;
  };
};

type B2BFormCompleteRequest = {
  token: string;
  user: string | number;
};

type B2BFormCompleteResponse = BaseSuccessResponse;

type RiskProfile = {
  id: number;
  is_enabled: boolean;
  name: string;
  description?: string;
  locked: boolean;
  created_at: string;
  updated_at: string;
  signals?: Record<string, any>[];
};

type GetRiskProfilesResponse = BaseSuccessResponse & {
  data: {
    profiles: RiskProfile[];
  };
};

type CreateRiskProfileRequest = {
  name: string;
  description: string;
};

type BaseRiskProfileRequest = {
  profileId: number;
};

type ActivateRiskProfileRequest = BaseRiskProfileRequest;

type RiskProfileResponse = {
  success: boolean;
  data: {
    profile: RiskProfile;
  };
};

type RiskProfileSignal = {
  key: string;
  score: number;
  received: string;
  sent: string;
};

type RiskProfileUpdateSignalsRequest = {
  profileId: number;
  signals: RiskProfileSignal[];
};

type RiskProfileUpdateRequest = CreateRiskProfileRequest & {
  profileId: number;
};

type OtpAuthEmailRequest = {
  email: string;
  companyName: string;
};

type OtpAuthEmailResponse = BaseSuccessResponse;

type OtpAuthOtpRequest = {
  email: string;
  otp: string;
  ref?: string;
  preference?: UserPreferenceEnum;
};

type OtpAuthResponse = CheckResponse;

type AmlPreviewRequest = {
  uid: string;
  ref?: string;
};

type AmlPreviewResponse = BaseSuccessResponse & {
  data: {
    uid: string;
    hash: string;
    network: string;
    riskScoreLevel: RiskScoreLevelEnum;
    blocklisted: boolean;
    exposures: Record<string, any>;
  };
};

type AmlTraceData = {
  blockchain: string;
  asset: string;
  hash: string;
  tokenId: string;
};

type AmlTraceRequest = {
  traces: AmlTraceData[];
};

type AmlTraceResponse = BaseSuccessResponse & {
  data: {
    data: AmlTraceHistoryItem;
  };
  balance: number;
  traceKey: string;
};

type AmlTraceListRequest =
  | {
      page?: number;
      queryHash?: string;
      filter?: Record<string, any>;
    }
  | Record<string, never>;

type AmlTraceListResponse = BaseSuccessResponse & {
  data: {
    result: boolean;
    pageLimit: number;
    totalCount: number;
    data: AmlTraceHistoryItem[];
  };
};

type TracerCurrenciesResponse = BaseSuccessResponse & {
  data: {
    currencies: string[];
  };
};

type AmlTraceHistoryItem = {
  uid: string;
  address?: string;
  tx: string;
  asset: string;
  flow: AmlFlowEnum;
  network: string;
  cost: number;
  status: AmlStatusEnum;
  timestamp: string;
  tokenDetails?: Record<string, any>;
  tokenId?: string;
  _type: number;
  info?: Record<string, any>;
};

type AmlPreviewData = {
  uid: string;
  address: string;
  network: ChainsEnum;
  riskScoreLevel: RiskScoreLevelEnum;
  blocklisted: boolean;
  exposures: Record<string, any>;
};

type WidgetUserRequest = {
  ref?: string;
};

type WidgetUserResponse = BaseSuccessResponse & {
  data: {
    username: string;
    uid: string;
    appId: string | null;
  };
};

export type {
  BaseRequest,
  BaseErrorResponse,
  BaseSuccessResponse,
  BaseErrorCodeResponse,
  CheckRequest,
  CheckResponse,
  SignupRequest,
  SignupRequestExtended,
  SignupResponse,
  SigninRequest,
  SigninResponse,
  GoogleAuthRequest,
  GoogleAuthResponse,
  TelegramAuthRequest,
  TelegramAuthRequestExtended,
  TelegramAuthResponse,
  SignoutRequest,
  SignoutResponse,
  ConfirmEmailRequest,
  ConfirmEmailResponse,
  SendConfirmationEmailRequest,
  SendConfirmationEmailResponse,
  ForgotPasswordEmailRequest,
  ForgotPasswordEmailResponse,
  VerifyRecoveryTokenRequest,
  VerifyRecoveryTokenResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  ChangeFullNameEmailRequest,
  UpdateLocaleRequest,
  UpdateLocaleResponse,
  UpdateUserResponse,
  ChangeUserPasswordRequest,
  GetTwoFACodeRequest,
  GetTwoFACodeResponse,
  UpdateTwoFAUsageRequest,
  UpdateTwoFAUsageResponse,
  UpdateCheckModeRequest,
  UpdateCheckModeResponse,
  ReceiveRemindersRequest,
  ReceiveRemindersResponse,
  AmlHistoryRequest,
  AmlHistoryResponse,
  ExportHistoryResponse,
  AmlCheckRequest,
  AmlCheckResponse,
  WhichHashRequest,
  WhichHashResponse,
  AmlCheck,
  AmlCheckItem,
  AmlHistory,
  AmlHistoryItem,
  InvestigationHistoryItem,
  User,
  TwoFACode,
  WhichHash,
  Profile,
  BalanceAlert,
  GetProfileInfoRequest,
  GetProfileInfoResponse,
  SetBalanceAlertRequest,
  SetBalanceAlertResponse,
  DeleteBalanceAlertRequest,
  DeleteBalanceAlertResponse,
  PromoHistoryRequest,
  PromoCodeItem,
  PromoHistoryResponse,
  IssuePromoCodeRequest,
  IssuePromoCodeResponse,
  ReferralLink,
  CreateReflinkRequest,
  UpdateReflinkRequest,
  ReflinkResponse,
  ServiceOptions,
  GetServiceOptionsRequest,
  GetServiceOptionsResponse,
  ExploreConnectionRequest,
  ExploreConnectionResponse,
  AssetToken,
  AssetTokenRequest,
  AssetTokenResponse,
  BonusItem,
  PriceList,
  Plan,
  GetPriceListRequest,
  GetPriceListResponse,
  EnterPromoCodeRequest,
  EnterPromoCodeResponse,
  PromoCodeBase,
  InvoiceRequest,
  StripeInvoiceResponse,
  StripeInvoice,
  InvoiceResponse,
  Invoice,
  InvoiceHistoryResponse,
  UserInvoiceHistoryData,
  GetTxReceiversRequest,
  GetTxReceiversResponse,
  GetAmlHistoryItemRequest,
  GetAmlHistoryItemResponse,
  CustomTariffRequest,
  CustomTariffResponse,
  GetAmlHistoryItemResultRequest,
  GetAmlHistoryItemResultResponse,
  ExternalPaymentRequest,
  ExternalPaymentResponse,
  ContactFormRequest,
  ContactFormResponse,
  CheckBatchItem,
  CreateCheckBatchRequest,
  CreateCheckBatchResponse,
  CheckBatchHistoryRequest,
  CheckBatchHistoryResponse,
  ExportBatchRequest,
  ExportBatchResponse,
  BatchStateRequest,
  BatchStateResponse,
  MonitoringSubscribeRequest,
  MonitoringSubscribeResponse,
  MonitoringUnsubscribeRequest,
  MonitoringUnsubscribeResponse,
  UserPreferencesRequest,
  UserPreferencesResponse,
  MonitoringAddress,
  AddressMonitoringListRequest,
  AddressMonitoringListResponse,
  UnsubscribeTokenVerifyRequest,
  UnsubscribeTokenVerifyResponse,
  UnsubscribeByTokenRequest,
  UnsubscribeByTokenResponse,
  TestEmailRequest,
  CompAuthTokenResponse,
  B2BFormTokenRequest,
  B2BFormTokenResponse,
  B2BFormCompleteRequest,
  B2BFormCompleteResponse,
  GetRiskProfilesResponse,
  RiskProfile,
  CreateRiskProfileRequest,
  BaseRiskProfileRequest,
  ActivateRiskProfileRequest,
  RiskProfileResponse,
  RiskProfileSignal,
  RiskProfileUpdateSignalsRequest,
  RiskProfileUpdateRequest,
  OtpAuthEmailRequest,
  OtpAuthEmailResponse,
  OtpAuthOtpRequest,
  OtpAuthResponse,
  GoogleAuthOneTapRequest,
  GoogleAuthOneTapResponse,
  AmlPreviewRequest,
  AmlPreviewResponse,
  AmlPreviewData,
  WidgetUserRequest,
  WidgetUserResponse,
  AmlTraceRequest,
  AmlTraceResponse,
  AmlTraceListRequest,
  AmlTraceListResponse,
  TracerCurrenciesResponse,
  AmlTraceHistoryItem,
};

export {
  EmailStatusEnum,
  AmlFlowEnum,
  AmlStatusEnum,
  AlertsPeriodEnum,
  TransactionDirectionEnum,
  SideEnum,
  CheckTypeEnum,
  BonusFlowValueEnum,
  BonusTypeEnum,
  CheckOutputTypeEnum,
  RiskScoreLevelEnum,
  AccountModesEnum,
  UserPreferenceEnum,
};
